import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_CHARS = 170;

export interface GuidelineDescriptionCellProps {
  text: string;
  maxChars?: number;
}

export function GuidelineDescriptionCell({
  text,
  maxChars = DEFAULT_MAX_CHARS,
}: GuidelineDescriptionCellProps) {
  const { t } = useTranslation('knowledge');

  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate = text.length > maxChars;
  const displayText = isExpanded || !shouldTruncate ? text : `${text.slice(0, maxChars)}...`;
  // The parent `DatatableCell` bakes in `whitespace-nowrap`, and the 170-character budget
  // bounds length, not rendered width: a run with no spaces (a URL, an id) still overflows
  // the fixed-width column at any character count. `break-words` escapes both. The line clamp
  // only applies while collapsed and truncated — it is a height bound for the wrapped text,
  // not a second truncation; expanding removes it so the full text can grow the row.
  const isClamped = shouldTruncate && !isExpanded;

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsExpanded((previous) => !previous);
  };

  return (
    <span
      className={`whitespace-normal break-words text-sm text-foreground ${isClamped ? 'line-clamp-3' : ''}`}
    >
      {displayText}
      {shouldTruncate && (
        <button
          type="button"
          onClick={handleToggle}
          className="ml-1 font-medium text-muted-foreground hover:underline"
        >
          {isExpanded
            ? t('guidelines.expandableDescription.showLess')
            : t('guidelines.expandableDescription.showMore')}
        </button>
      )}
    </span>
  );
}

GuidelineDescriptionCell.displayName = 'GuidelineDescriptionCell';
